import React, { useState } from 'react';
import type { CrewMember, Document } from '../types';
import { getCertificateStatusFromDocuments, CertStatus } from '../utils/certificateUtils';

interface AddCrewModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableCrew: CrewMember[];
  documents: Document[];
  onAddCrew: (crewIds: string[]) => void;
  adding: boolean;
}

/** Render certificate status icon */
const CertIcon: React.FC<{ status: CertStatus }> = ({ status }) => {
  if (status === 'valid') {
    return <span className="cert-tick" title="Valid certificate">✓</span>;
  }
  if (status === 'expired') {
    return <span className="cert-expired" title="Certificate expired">⚠️</span>;
  }
  return null;
};

/**
 * Modal for selecting multiple crew members to add to an event.
 * Requirements: 3.2, 3.3, 3.4
 */
const AddCrewModal: React.FC<AddCrewModalProps> = ({
  isOpen,
  onClose,
  availableCrew,
  documents,
  onAddCrew,
  adding,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const toggleSelect = (crewId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(crewId)) next.delete(crewId);
      else next.add(crewId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === availableCrew.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(availableCrew.map(m => m.crewId)));
    }
  };

  const handleAdd = () => {
    if (selected.size === 0) return;
    onAddCrew(Array.from(selected));
    setSelected(new Set());
  };

  const allSelected = availableCrew.length > 0 && selected.size === availableCrew.length;

  return (
    <div className="modal-overlay">
      <div className="modal-content add-crew-modal">
        <div className="modal-header">
          <h2>Add Crew Members</h2>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {availableCrew.length === 0 ? (
            <div className="add-crew-empty">
              <p>All crew members from your master list are already assigned to this event.</p>
              <p>Add more crew members in the Crew page first.</p>
            </div>
          ) : (
            <table className="add-crew-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all crew members"
                    />
                  </th>
                  <th>Name</th>
                  <th className="cert-col">SSSC</th>
                  <th className="cert-col">First Aid</th>
                  <th className="cert-col">Radio</th>
                </tr>
              </thead>
              <tbody>
                {availableCrew.map((member) => {
                  const certStatus = getCertificateStatusFromDocuments(member.crewId, documents);
                  const isSelected = selected.has(member.crewId);

                  return (
                    <tr
                      key={member.crewId}
                      className={isSelected ? 'selected-row' : ''}
                      onClick={() => toggleSelect(member.crewId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="checkbox-col">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(member.crewId)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${member.firstName} ${member.lastName}`}
                        />
                      </td>
                      <td>{member.firstName} {member.lastName}</td>
                      <td className="cert-col"><CertIcon status={certStatus.sssc} /></td>
                      <td className="cert-col"><CertIcon status={certStatus.firstAid} /></td>
                      <td className="cert-col"><CertIcon status={certStatus.radio} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer">
          <button className="secondary-button" onClick={onClose}>Cancel</button>
          <button
            className="primary-button"
            onClick={handleAdd}
            disabled={selected.size === 0 || adding}
          >
            {adding ? 'Adding...' : `Add ${selected.size > 0 ? `(${selected.size})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCrewModal;
